import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/services/api';
import { useChartTheme } from '@/components/base/chartTheme';
import ChartLegend from '@/components/base/ChartLegend';
import { useTheme } from '@/hooks/useTheme';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

interface TimeseriesPoint {
  date: string;
  base_price?: number | null;
  block_price?: number | null;
  peak_price?: number | null;
  base_volume_mwh?: number | null;
  block_volume_mwh?: number | null;
}

interface TimeseriesResponse {
  windowDays: number;
  rows: number;
  stats: {
    base?: { wavgPrice: number | null; totalMwh: number; positions: number };
    block?: { wavgPrice: number | null; totalMwh: number; positions: number };
    peak?: { wavgPrice: number | null; totalMwh: number; positions: number };
  };
  points: TimeseriesPoint[];
}

interface SummaryResponse {
  solarGo: { avgAsk30d: number | string | null; changePct: number | null; sellPositions30d: number; closedDeals30d: number; offeredMwh30d: number | string | null; marketState: string };
  latestAuctionDate: string;
}

const fmtNum = (v: any, digits = 0): string => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString('uk-UA', { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

const shortDate = (d: string): string => {
  if (!d) return '';
  const [, m, day] = d.split('-');
  return `${day}.${m}`;
};

export default function UeexMarketWidget() {
  const ct = useChartTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [data, setData] = useState<TimeseriesResponse | null>(null);
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [windowDays, setWindowDays] = useState<7 | 14 | 30>(14);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const load = async () => {
      try {
        const [ts, sm] = await Promise.all([
          api.get<TimeseriesResponse>(`/market/ueex/electricity-timeseries?days=${windowDays}`),
          api.get<SummaryResponse>('/market/ueex/summary'),
        ]);
        if (!cancelled) { setData(ts); setSummary(sm); setError(null); }
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'load failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const interval = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [windowDays]);

  const baseStats = data?.stats?.base;
  const blockStats = data?.stats?.block;
  const peakStats = data?.stats?.peak;
  const points = data?.points ?? [];

  const premiumPct =
    baseStats?.wavgPrice && blockStats?.wavgPrice
      ? (((baseStats.wavgPrice - blockStats.wavgPrice) / blockStats.wavgPrice) * 100)
      : null;

  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const kpiBg = isDark ? 'rgba(255,255,255,0.03)' : '#F9F9FB';
  const toggleBg = isDark ? 'rgba(255,255,255,0.05)' : '#F9F9FB';

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${cardBorder}` }}>
        <div className="flex items-center gap-2">
          <i className="ri-line-chart-line text-base" style={{ color: '#0066FF' }}></i>
          <div>
            <h3 className="font-semibold text-sm" style={{ color: textMain }}>
              {t('ueex_widget_title')}
            </h3>
            <div className="text-[10px]" style={{ color: textMuted }}>{t('ueex_widget_subtitle')}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Period toggle */}
          <div className="flex rounded text-[11px] overflow-hidden" style={{ border: `1px solid ${cardBorder}` }}>
            {([7, 14, 30] as const).map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setWindowDays(d)}
                className={`px-2 py-1 cursor-pointer transition whitespace-nowrap ${
                  windowDays === d ? 'text-white' : ''
                }`}
                style={{
                  background: windowDays === d ? '#0066FF' : toggleBg,
                  color: windowDays === d ? '#fff' : textMuted,
                }}
              >
                {d}{t('ueex_widget_days_suffix')}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => navigate('/market/ueex')}
            className="text-xs hover:underline cursor-pointer whitespace-nowrap"
            style={{ color: '#0066FF' }}
          >
            {t('ueex_widget_view_all')} →
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-4 pt-4">
        <KpiCell
          label="BASE (24/7)"
          value={fmtNum(baseStats?.wavgPrice, 0)}
          unit="грн/МВт·год"
          subtitle={`${fmtNum(baseStats?.totalMwh, 0)} МВт·год • ${baseStats?.positions ?? 0} ${t('ueex_widget_deals')}`}
          color="#0066FF"
          isDark={isDark}
        />
        <KpiCell
          label={t('ueex_widget_block_label')}
          value={fmtNum(blockStats?.wavgPrice, 0)}
          unit="грн/МВт·год"
          subtitle={`${fmtNum(blockStats?.totalMwh, 0)} МВт·год • ${blockStats?.positions ?? 0} ${t('ueex_widget_deals')}`}
          color="#8B5CF6"
          isDark={isDark}
        />
        <KpiCell
          label="PEAK"
          value={peakStats?.wavgPrice ? fmtNum(peakStats.wavgPrice, 0) : '—'}
          unit="грн/МВт·год"
          subtitle={peakStats ? `${fmtNum(peakStats.totalMwh, 0)} МВт·год` : t('ueex_widget_no_peak')}
          color="#F59E0B"
          isDark={isDark}
        />
        <KpiCell
          label={t('ueex_widget_solar_label')}
          value={fmtNum(summary?.solarGo?.avgAsk30d, 2)}
          unit="грн/МВт·год (ask)"
          subtitle={`${summary?.solarGo?.sellPositions30d ?? 0} ${t('ueex_widget_positions')} • ${summary?.solarGo?.closedDeals30d ?? 0} ${t('ueex_widget_deals')}`}
          color="#10B981"
          isDark={isDark}
        />
      </div>

      {/* Chart */}
      <div className="px-4 pt-3 pb-2">
        {loading && (
          <div className="h-[240px] flex items-center justify-center text-xs" style={{ color: textMuted }}>
            {t('ueex_widget_loading')}
          </div>
        )}
        {error && <div className="h-[240px] flex items-center justify-center text-xs text-[var(--err)]">{error}</div>}
        {!loading && !error && points.length === 0 && (
          <div className="h-[240px] flex items-center justify-center text-xs" style={{ color: textMuted }}>
            {t('ueex_widget_no_recent_data')}
          </div>
        )}
        {!loading && !error && points.length > 0 && (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={points} margin={{ top: 5, right: 16, bottom: 5, left: 0 }}>
              <CartesianGrid {...ct.gridProps} />
              <XAxis dataKey="date" tickFormatter={shortDate} {...ct.axis} />
              <YAxis
                {...ct.axis}
                label={ct.axisLabel('грн/МВт·год')}
              />
              <Tooltip
                formatter={(v: any) => `${fmtNum(v, 0)} грн`}
                labelFormatter={(l: string) => {
                  const [y, m, day] = l.split('-');
                  return `${day}.${m}.${y}`;
                }}
                {...ct.tooltip} cursor={ct.cursorLine}
              />
              <Line dataKey="base_price" name="BASE (24/7)" stroke={ct.series.s3} {...ct.line} />
              <Line dataKey="block_price" name="Блочна" stroke={ct.series.warn} {...ct.line} />
              <Line dataKey="peak_price" name="PEAK" stroke={ct.series.ok} {...ct.line} />
            </LineChart>
          </ResponsiveContainer>
        )}
        {!loading && !error && points.length > 0 && (
          <ChartLegend items={[
            { label: 'BASE (24/7)', color: ct.series.s3 },
            { label: 'Блочна', color: ct.series.warn },
            { label: 'PEAK', color: ct.series.ok },
          ]} />
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 flex items-center justify-between text-[10px]" style={{ borderTop: `1px solid ${cardBorder}`, color: textMuted }}>
        <div>
          {premiumPct !== null && (
            <span>
              BASE premium: <span className="font-semibold" style={{ color: premiumPct > 0 ? '#10B981' : '#EF4444' }}>
                {premiumPct > 0 ? '+' : ''}{premiumPct.toFixed(1)}%
              </span>
            </span>
          )}
        </div>
        <div>
          {t('ueex_widget_source')} • {summary?.latestAuctionDate?.slice(0, 10) ?? '—'}
        </div>
      </div>
    </div>
  );
}

function KpiCell({ label, value, unit, subtitle, color, isDark }: {
  label: string; value: string; unit: string; subtitle: string; color: string; isDark: boolean;
}) {
  return (
    <div className="rounded p-2.5 border-l-2" style={{ borderLeftColor: color, background: isDark ? 'rgba(255,255,255,0.03)' : '#F9F9FB' }}>
      <div className="text-[10px] uppercase tracking-wide" style={{ color: isDark ? '#6B6B70' : '#6B7280' }}>{label}</div>
      <div className="font-mono text-base font-semibold mt-0.5" style={{ color: isDark ? '#E0E0E0' : '#1A1A1A' }}>
        {value}
        <span className="text-[10px] font-normal ml-1" style={{ color: isDark ? '#6B6B70' : '#9CA3AF' }}>{unit}</span>
      </div>
      <div className="text-[10px] mt-0.5 truncate" style={{ color: isDark ? '#6B6B70' : '#9CA3AF' }} title={subtitle}>{subtitle}</div>
    </div>
  );
}